import * as React from 'react';
import {useRoute} from 'react-router5';
import {toast} from 'react-toastify';
import {Formik, Form, Field, FieldProps} from 'formik';
import * as Yup from 'yup';
import AuthStore, {SignInWithUsernameAndPasswordData} from 'stores/AuthStore';
import Row from 'components/Row';
import Column from 'components/Column';
import Input from 'components/Input';
import Button, {ButtonVariant} from 'components/Button';
import Link from 'components/Link';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import * as helpers from '../helpers';
import styled from "styled-components";
import SocialButton, {SocialType} from "components/SocialButton";

export type SignInValues
  = SignInWithUsernameAndPasswordData;

export interface SignInProps {
  initialValues?: SignInValues;
  renderAfter?: JSX.Element;
}

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-top: 8vh;
  display: flex;
  justify-content: center;
`

/**
 * This is a component of the sign in.
 * @param {SignInProps} props
 * @return {React.ReactElement<SignInProps>}
 * @constructor
 */
export default function SignIn(
    props: SignInProps,
): React.ReactElement<SignInProps> {
  const {router} = useRoute();

  const initialValues: SignInValues = props.initialValues || {
    username: 'reworkteacher',
    password: '12345678',
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Введите логин'),
    password: Yup.string().required('Введите пароль'),
  });

  /**
   * This function render socials auth component.
   * @return {JSX.Element}
   */
  function renderSocials(): JSX.Element {
    return (
      <helpers.SocialsContainer>
        <helpers.SocialsTitle>Войти с помощью</helpers.SocialsTitle>
        <helpers.SocialsList>
          <SocialButton type={SocialType.Vkontakte}/>
          <SocialButton type={SocialType.Apple}/>
          <SocialButton type={SocialType.Facebook}/>
          <SocialButton type={SocialType.Google}/>
        </helpers.SocialsList>
      </helpers.SocialsContainer>
    );
  }

  /**
   * This function will render rules.
   * @return {JSX.Element}
   */
  function renderRules(): JSX.Element {
    return (
      <helpers.Text>
        Нажимая “Войти”, вы принимаете
        <Link href="/">
          Условия Пользовательского соглашения
        </Link>
        {'и'}
        <Link href="/">
          Политики конфиденциальности.
        </Link>
      </helpers.Text>
    );
  }

  /**
   * This function will render after content.
   * @return {JSX.Element | undefined}
   */
  function renderAfterContent(): JSX.Element | undefined {
    return props.renderAfter;
  }

  /**
   * This form submit event handler.
   * @param {SignInValues} values
   */
  function handleSubmit(values: SignInValues): void {
    AuthStore.signInWithUsernameAndPassword(values)
        .then((response) => {
          validateResponse(response);
          if (response && !isError(response)) {
            router.navigate('dashboard');
            toast.success('Добро пожаловать!');
          }
        });
  }

  return (
    <Container>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {() => (
          <Form>
            <helpers.Container>
              <helpers.Title>
                Авторизация
              </helpers.Title>
              <helpers.Rows>
                <Row>
                  <Column>
                    <Field name="username">
                      {({field, meta}: FieldProps) => (
                        <Input
                          {...field}
                          type="text"
                          label="Логин"
                          error={meta.touched ? (
                            meta.error
                          ) : undefined}
                          placeholder="Логин"
                          required
                        />
                      )}
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Field name="password">
                      {({field, meta}: FieldProps) => (
                        <Input
                          {...field}
                          type="password"
                          label="Пароль"
                          error={meta.touched ? (
                            meta.error
                          ) : undefined}
                          placeholder="Пароль"
                          required
                        />
                      )}
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Button
                      type="submit"
                      variant={ButtonVariant.Primary}
                      stretched
                    >
                      Войти
                    </Button>
                  </Column>
                </Row>
              </helpers.Rows>
              {renderSocials()}
              {renderRules()}
              {renderAfterContent()}
            </helpers.Container>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
